import { apiUrl } from "../utils/api";

/**
 * „Courté Intern" / News (DD4). Text + Bilder, Ziel TENANT (alle) oder TEAM,
 * gelesen/ungelesen je Mitarbeiter. Pflege (Liste/Anlegen/Bearbeiten/Löschen)
 * nur admin/gfgl; Lesen + als-gelesen-markieren jeder authentifizierte Nutzer.
 */

export type AnnouncementTargetType = "TENANT" | "TEAM";

export interface Announcement {
  id: string;
  tenantId: string;
  title: string;
  body: string;
  /** /media-Refs der hochgeladenen Bilder (z. B. "/media/news/<file>"). */
  images?: string[] | null;
  targetType: AnnouncementTargetType;
  targetTeamId?: string | null;
  createdByUserId: string;
  createdAt: string;
  deleted: boolean;
}

/** Wie {@link Announcement}, zusätzlich mit dem persönlichen gelesen-Status. */
export type AnnouncementForUser = Announcement & { read: boolean };

export interface AnnouncementInput {
  title: string;
  body: string;
  images?: string[] | null;
  targetType?: AnnouncementTargetType;
  targetTeamId?: string | null;
}

async function req<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`API error ${res.status}: ${txt}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const announcementService = {
  /** Sichtbare News des Nutzers (TENANT-weit + eigene Teams), je mit `read`. */
  async listMine(): Promise<AnnouncementForUser[]> {
    return req<AnnouncementForUser[]>(apiUrl("announcement/mine"));
  },

  /** Alle News des Mandanten für die Pflege-Liste (nur admin/gfgl). */
  async listAll(): Promise<Announcement[]> {
    return req<Announcement[]>(apiUrl("announcement"));
  },

  async create(input: AnnouncementInput): Promise<Announcement> {
    return req<Announcement>(apiUrl("announcement"), {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async update(id: string, input: AnnouncementInput): Promise<Announcement> {
    return req<Announcement>(apiUrl(`announcement/${id}`), {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  async remove(id: string): Promise<void> {
    await req<{ ok: true }>(apiUrl(`announcement/${id}`), { method: "DELETE" });
  },

  async markRead(id: string): Promise<void> {
    await req<{ ok: true }>(apiUrl(`announcement/${id}/read`), { method: "POST" });
  },

  /**
   * Lädt ein Bild für eine News hoch (multipart, Feldname `file`) und liefert die
   * api-relative `/media/news/<file>`-Ref, die in `images` gespeichert wird.
   */
  async uploadImage(file: File): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(apiUrl("upload/news-image"), {
      method: "POST",
      body: fd,
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Upload-Fehler ${res.status}: ${txt}`);
    }
    const data = (await res.json()) as { url: string };
    return data.url;
  },
};
